// src/features/captions/components/GenerateTermsModal.tsx

import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconInfoCircle, IconPlus, IconX } from '@tabler/icons-react';
import { useController, useForm, type Control } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@/shared/api/ApiError';
import { formatTimestamp } from '@/shared/utils/date';
import {
  CAPTION_GENERATE_CATEGORIES,
  CAPTION_GENERATE_CONSTRAINTS,
} from '@/lib/constants/captions';
import { CaptionTermUtils } from '../CaptionTermUtils';
import { useCaptionGenerations } from '../useCaptionTerms';
import { useGenerateCaptionTerms } from '../useCaptionMutations';
import {
  GenerateTermsSchema,
  type GenerateTermsFormData,
} from '../schemas/GenerateTermsForm.schema';
import { CaptionTermsEditor } from './CaptionTermsEditor';
import type { CaptionGenerationData } from '../types';

const DEFAULT_FORM_VALUES: GenerateTermsFormData = {
  orgName: '',
  category: 'sports',
  categoryOther: '',
  location: '',
  sourceUrls: ['', '', '', ''],
  contextNote: '',
};

const CATEGORY_OPTIONS = CAPTION_GENERATE_CATEGORIES.map((c) => ({
  value: c.value,
  label: c.label,
}));

type ModalView = { mode: 'form' } | { mode: 'review'; generation: CaptionGenerationData };

/** A submitted run awaiting its result via the generations poll. */
interface PendingRun {
  orgName: string;
  submittedAtEpochSec: number;
}

interface GenerateTermsModalProps {
  opened: boolean;
  onClose: () => void;
  /** Fills the account terms editor with the reviewed terms (never auto-saves). */
  onApply: (terms: string[]) => void;
}

/**
 * AI term-research flow: form → long-running progress → review draft with
 * removable term chips → apply to the account editor. Past runs re-open from
 * the Recent generations list. This component stays mounted while its Modal is
 * closed, so a run keeps going (and lands on the review panel) even if the
 * user closes mid-research.
 *
 * The api ALB's 60 s idle timeout DROPS the held POST response on every real
 * run (1–8 min of silent research) — the run always completes server-side and
 * lands in the generations list. So the POST is fire-and-mostly-forget: its
 * network failure is EXPECTED, and the flow resolves by polling generations
 * for the submitted run to leave 'running'. Real error responses that do
 * arrive (400/403/429-quota/502/503) still short-circuit with their copy;
 * 429 already-in-flight joins the poll instead.
 */
export function GenerateTermsModal({ opened, onClose, onApply }: GenerateTermsModalProps) {
  const generateMutation = useGenerateCaptionTerms();

  const [view, setView] = useState<ModalView>({ mode: 'form' });
  const [reviewTerms, setReviewTerms] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pendingRun, setPendingRun] = useState<PendingRun | null>(null);

  const { generations, isLoading: generationsLoading, dataUpdatedAt } = useCaptionGenerations(
    opened,
    pendingRun != null,
  );

  const { control, watch, handleSubmit, getValues, setValue } = useForm<GenerateTermsFormData>({
    resolver: zodResolver(GenerateTermsSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const watchedCategory = watch('category');
  const watchedOrgName = watch('orgName');
  const contextNoteLength = watch('contextNote')?.length ?? 0;

  // Source URL guidance matches the selected category — URLs are the
  // highest-leverage research input, so the examples should fit the domain.
  const urlHint =
    CAPTION_GENERATE_CATEGORIES.find((c) => c.value === watchedCategory)?.urlHint ??
    'Pages listing names and terms — rosters, staff pages, lineups, agendas.';

  // Progressive disclosure over the schema's fixed 4-slot array: one visible
  // URL field, "Add another" up to the max. Removing a row shifts later
  // values up so hidden slots never hold stale text.
  const [urlCount, setUrlCount] = useState(1);
  const removeUrl = (index: number) => {
    const next = getValues('sourceUrls').filter((_, i) => i !== index);
    while (next.length < CAPTION_GENERATE_CONSTRAINTS.SOURCE_URLS_MAX) next.push('');
    setValue('sourceUrls', next);
    setUrlCount((c) => Math.max(1, c - 1));
  };

  // Quota already spent this month (server window: calendar month, UTC).
  // Cache-served reruns create no generation row, so they don't count.
  const runsThisMonth = CaptionTermUtils.countRunsThisMonthUtc(
    generations,
    Math.floor(Date.now() / 1000),
  );

  const showRunResult = (generation: CaptionGenerationData) => {
    if (generation.status === 'ok') {
      setReviewTerms(generation.terms ?? []);
      setView({ mode: 'review', generation });
    } else {
      setErrorMessage(CaptionTermUtils.describeGenerationRunError(generation));
    }
  };

  // Outcome channel: each poll tick, resolve the pending run against the list.
  useEffect(() => {
    if (!pendingRun) return;
    const outcome = CaptionTermUtils.resolveGenerationOutcome(
      generations,
      pendingRun.orgName,
      pendingRun.submittedAtEpochSec,
    );
    if (outcome) {
      setPendingRun(null);
      showRunResult(outcome);
      return;
    }
    const waitedSec = Math.floor(Date.now() / 1000) - pendingRun.submittedAtEpochSec;
    if (waitedSec > CAPTION_GENERATE_CONSTRAINTS.GENERATION_GIVE_UP_SEC) {
      setPendingRun(null);
      setErrorMessage(
        'The research run is taking longer than expected. Check Recent generations in a few minutes — the result appears there when it finishes.',
      );
    }
    // dataUpdatedAt: re-evaluate on every poll tick — structural sharing keeps
    // `generations` referentially stable when the list content is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generations, pendingRun, dataUpdatedAt]);

  const onSubmit = async (data: GenerateTermsFormData) => {
    setErrorMessage(null);
    const run: PendingRun = {
      orgName: data.orgName.trim(),
      submittedAtEpochSec: Math.floor(Date.now() / 1000),
    };
    setPendingRun(run);
    try {
      // Held responses only survive for fast paths (cache hits, quick 4xx) —
      // when one does arrive it wins over the poll.
      const res = await generateMutation.mutateAsync(CaptionTermUtils.buildGenerateRequest(data));
      setPendingRun(null);
      showRunResult(res);
    } catch (err) {
      if (err instanceof ApiError) {
        const inFlight = err.httpStatusCode === 429 && /in flight/i.test(err.message);
        if (!inFlight) {
          setPendingRun(null);
          setErrorMessage(CaptionTermUtils.describeGenerateError(err.httpStatusCode, err.message));
        }
        // in-flight: someone's run is already going — the poll resolves it.
      }
      // Non-ApiError = network-level (the ALB reset) — expected; keep polling.
    }
  };

  const openPastGeneration = (generation: CaptionGenerationData) => {
    setErrorMessage(null);
    setReviewTerms(generation.terms ?? []);
    setView({ mode: 'review', generation });
  };

  const handleApply = () => {
    onApply(reviewTerms);
    setView({ mode: 'form' });
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Generate with AI research"
      size="lg"
      closeOnClickOutside={false}
    >
      {generateMutation.isPending || pendingRun != null ? (
        <Stack align="center" py="xl" gap="sm">
          <Loader />
          <Text fw={600}>Researching {watchedOrgName.trim() || 'your organization'}...</Text>
          <Text size="sm" c="dimmed" ta="center" maw={420}>
            AI research typically takes 1 to 3 minutes. You can close this window —
            the result appears here and under Recent generations when it finishes.
          </Text>
        </Stack>
      ) : view.mode === 'review' ? (
        <Stack gap="md">
          <Group gap="xs">
            <Text fw={600}>{view.generation.org_name}</Text>
            {view.generation.status && (
              <Badge variant="light" tt="none">{view.generation.status}</Badge>
            )}
            {view.generation.cached && (
              <Badge variant="light" color="gray" tt="none">recent result reused</Badge>
            )}
          </Group>

          {view.generation.rationale && (
            <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />} title="Research notes">
              <ScrollArea.Autosize mah={140}>
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{view.generation.rationale}</Text>
              </ScrollArea.Autosize>
            </Alert>
          )}

          <CaptionTermsEditor
            label="Generated terms"
            value={reviewTerms}
            onChange={setReviewTerms}
          />
          <Text size="xs" c="dimmed">
            Remove anything that doesn't fit. Applying fills the account terms editor —
            nothing is saved until you save there.
          </Text>

          <Group justify="space-between">
            <Button variant="default" onClick={() => setView({ mode: 'form' })}>
              Back
            </Button>
            <Button onClick={handleApply} disabled={reviewTerms.length === 0}>
              Apply to account terms
            </Button>
          </Group>
        </Stack>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              AI research finds the names and terms your captions need — people, places,
              teams, programs — from public sources about your organization.
            </Text>

            <ControlledTextInput
              control={control}
              name="orgName"
              label="Organization name"
              placeholder="e.g. Yakima Sports Network"
              maxLength={CAPTION_GENERATE_CONSTRAINTS.ORG_NAME_MAX}
              required
            />

            <CategorySelect control={control} />
            {watchedCategory === 'other' && (
              <ControlledTextInput
                control={control}
                name="categoryOther"
                label="Describe the category"
                placeholder="e.g. Esports league"
                maxLength={CAPTION_GENERATE_CONSTRAINTS.ORG_NAME_MAX}
              />
            )}

            <ControlledTextInput
              control={control}
              name="location"
              label="Location"
              description="City and state or region — helps identify the right organization."
              placeholder="e.g. Yakima, WA"
              maxLength={CAPTION_GENERATE_CONSTRAINTS.ORG_NAME_MAX}
            />

            <Stack gap="xs">
              {Array.from({ length: urlCount }, (_, i) => (
                <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
                  <Box style={{ flex: 1 }}>
                    <ControlledTextInput
                      control={control}
                      name={`sourceUrls.${i}`}
                      label={i === 0 ? 'Source URLs' : undefined}
                      description={i === 0 ? urlHint : undefined}
                      placeholder="https://"
                    />
                  </Box>
                  {i > 0 && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={() => removeUrl(i)}
                      aria-label="Remove URL"
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  )}
                </Group>
              ))}
              {urlCount < CAPTION_GENERATE_CONSTRAINTS.SOURCE_URLS_MAX && (
                <Button
                  variant="subtle"
                  size="compact-sm"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setUrlCount((c) => c + 1)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add another URL
                </Button>
              )}
            </Stack>

            <ControlledContextNote control={control} length={contextNoteLength} />

            {errorMessage && (
              <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
                {errorMessage}
              </Alert>
            )}

            <Group justify="space-between" align="center">
              {generationsLoading ? (
                <div />
              ) : (
                <Text size="xs" c="dimmed">
                  {runsThisMonth} of {CAPTION_GENERATE_CONSTRAINTS.MONTHLY_LIMIT} research runs
                  used this month
                </Text>
              )}
              <Group gap="sm">
                <Button variant="default" onClick={onClose}>Cancel</Button>
                <Button type="submit">Start research</Button>
              </Group>
            </Group>

            {generations.length > 0 && (
              <>
                <Divider label="Recent generations" labelPosition="left" />
                <Stack gap="xs">
                  {generations.map((generation) => (
                    <Group key={generation.generation_id} justify="space-between" wrap="nowrap">
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={500} truncate>
                          {generation.org_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {generation.created_epoch != null
                            ? formatTimestamp(generation.created_epoch)
                            : ''}
                          {' · '}
                          {generation.term_count} term{generation.term_count === 1 ? '' : 's'}
                          {' · '}
                          {generation.status}
                        </Text>
                      </Stack>
                      <Button
                        variant="subtle"
                        size="compact-sm"
                        onClick={() => openPastGeneration(generation)}
                      >
                        View
                      </Button>
                    </Group>
                  ))}
                </Stack>
              </>
            )}
            {generationsLoading && <Loader size="sm" />}
          </Stack>
        </form>
      )}
    </Modal>
  );
}

// ── Controlled form primitives (RHF + Mantine, useController per convention) ──

function ControlledTextInput({
  control,
  name,
  label,
  description,
  placeholder,
  maxLength,
  required,
}: {
  control: Control<GenerateTermsFormData>;
  name: 'orgName' | 'categoryOther' | 'location' | `sourceUrls.${number}`;
  label?: string;
  description?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}) {
  const { field, fieldState } = useController({ control, name });
  return (
    <TextInput
      label={label}
      description={description}
      placeholder={placeholder}
      value={(field.value as string) ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={maxLength}
      withAsterisk={required}
    />
  );
}

function CategorySelect({ control }: { control: Control<GenerateTermsFormData> }) {
  const { field } = useController({ control, name: 'category' });
  return (
    <Select
      label="Category"
      data={CATEGORY_OPTIONS}
      value={field.value}
      onChange={(val) => {
        if (val) field.onChange(val);
      }}
      onBlur={field.onBlur}
      allowDeselect={false}
    />
  );
}

function ControlledContextNote({
  control,
  length,
}: {
  control: Control<GenerateTermsFormData>;
  length: number;
}) {
  const { field, fieldState } = useController({ control, name: 'contextNote' });
  return (
    <Textarea
      label="Context note"
      description={`Anything else the research should know. ${length}/${CAPTION_GENERATE_CONSTRAINTS.CONTEXT_NOTE_MAX}`}
      value={field.value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={fieldState.error?.message}
      maxLength={CAPTION_GENERATE_CONSTRAINTS.CONTEXT_NOTE_MAX}
      rows={2}
    />
  );
}
